import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Wizard,
  type WizardProps,
} from '@cloudscape-design/components';
import { resolveOnboardingToken, updateOnboardingStep } from '../api/onboarding';

interface StepState {
  authEmail: string;
  authPassword: string;
  authComplete: boolean;
  awsAccountId: string;
  awsRegion: string;
  awsConnected: boolean;
  cfnLaunched: boolean;
  pcCredential: string;
  pcConnected: boolean;
  roleDiscoveryRun: boolean;
  roleDiscoveryResults: string[];
  gapAnalysisRun: boolean;
  gapAnalysisResults: string[];
}

const INITIAL_STATE: StepState = {
  authEmail: '',
  authPassword: '',
  authComplete: false,
  awsAccountId: '',
  awsRegion: 'us-east-1',
  awsConnected: false,
  cfnLaunched: false,
  pcCredential: '',
  pcConnected: false,
  roleDiscoveryRun: false,
  roleDiscoveryResults: [],
  gapAnalysisRun: false,
  gapAnalysisResults: [],
};

export function OnboardingPortal() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [tenantId, setTenantId] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [state, setState] = useState<StepState>(INITIAL_STATE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setError('No onboarding token provided.'); setLoading(false); return; }
    resolveOnboardingToken(token)
      .then((data) => {
        setTenantId(data.tenantId);
        setProductCode(data.productCode);
        setCurrentStep(data.step);
        if (data.awsAccountId) {
          setState((s) => ({ ...s, awsAccountId: data.awsAccountId || '' }));
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function advanceStep(nextStep: number) {
    setSaving(true);
    try {
      await updateOnboardingStep(tenantId, productCode, {
        step: nextStep,
        status: nextStep >= 6 ? 'completed' : 'in_progress',
      });
      setCurrentStep(nextStep);
    } catch (err) {
      console.error('Failed to advance step:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box padding="xxl" textAlign="center">
        <StatusIndicator type="loading">Resolving your onboarding session...</StatusIndicator>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="xxl">
        <Alert type="error" header="Onboarding Error">{error}</Alert>
      </Box>
    );
  }

  const productName = productCode === 'aeos-pc3' ? 'AEOS PC3' : productCode;

  const steps: WizardProps.Step[] = [
    {
      title: 'Welcome',
      description: `Set up ${productName} for your organization`,
      content: (
        <Container>
          <SpaceBetween size="m">
            <Header variant="h2">Welcome to {productName}</Header>
            <Box variant="p">
              This wizard will guide you through connecting your AWS account and deploying
              {productName} into your infrastructure. The entire process takes under 8 minutes.
            </Box>
            <ColumnLayout columns={3}>
              <div>
                <Box variant="awsui-key-label">Tenant ID</Box>
                <Box><code style={{ fontSize: 11 }}>{tenantId}</code></Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Product</Box>
                <Box>{productName}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Estimated Time</Box>
                <Box>~8 minutes</Box>
              </div>
            </ColumnLayout>
            <ProgressBar
              value={0}
              label="Onboarding Progress"
              description="Step 0 of 6"
            />
          </SpaceBetween>
        </Container>
      ),
    },
    {
      title: 'Authentication',
      description: 'Create your admin account',
      content: (
        <Container header={<Header variant="h2">Create Admin Account</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Your first user becomes the Admin automatically. SSO can be configured later.
            </Box>
            <FormField label="Email">
              <Input
                type="email"
                value={state.authEmail}
                onChange={({ detail }) => setState((s) => ({ ...s, authEmail: detail.value }))}
                placeholder="admin@yourcompany.com"
              />
            </FormField>
            <FormField label="Password" description="Minimum 12 characters">
              <Input
                type="password"
                value={state.authPassword}
                onChange={({ detail }) => setState((s) => ({ ...s, authPassword: detail.value }))}
              />
            </FormField>
            {state.authComplete && (
              <StatusIndicator type="success">Account created</StatusIndicator>
            )}
            {!state.authComplete && (
              <Button
                variant="primary"
                disabled={!state.authEmail || state.authPassword.length < 12}
                onClick={() => setState((s) => ({ ...s, authComplete: true }))}
              >
                Create Account
              </Button>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: false,
    },
    {
      title: 'Connect AWS Account',
      description: 'Deploy infrastructure via CloudFormation',
      content: (
        <Container header={<Header variant="h2">Connect Your AWS Account</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Click the button below to launch a CloudFormation stack in your AWS account.
              This creates a scoped IAM role — no manual IAM configuration needed.
            </Box>
            <Alert type="info">
              We recommend using a dedicated member account, not your management or production account.
            </Alert>
            <FormField label="AWS Account ID">
              <Input
                value={state.awsAccountId}
                onChange={({ detail }) => setState((s) => ({ ...s, awsAccountId: detail.value }))}
                placeholder="123456789012"
              />
            </FormField>
            <FormField label="Region">
              <Input
                value={state.awsRegion}
                onChange={({ detail }) => setState((s) => ({ ...s, awsRegion: detail.value }))}
              />
            </FormField>
            {!state.cfnLaunched ? (
              <Button
                variant="primary"
                disabled={!state.awsAccountId || state.awsAccountId.length !== 12}
                onClick={() => setState((s) => ({ ...s, cfnLaunched: true }))}
                iconName="external"
              >
                Launch CloudFormation Stack
              </Button>
            ) : !state.awsConnected ? (
              <SpaceBetween size="s">
                <StatusIndicator type="in-progress">
                  Waiting for CloudFormation stack to complete...
                </StatusIndicator>
                <Button onClick={() => setState((s) => ({ ...s, awsConnected: true }))}>
                  I've approved the stack
                </Button>
              </SpaceBetween>
            ) : (
              <StatusIndicator type="success">AWS account connected</StatusIndicator>
            )}
          </SpaceBetween>
        </Container>
      ),
    },
    {
      title: 'Partner Central',
      description: 'Connect your AWS Partner Central account',
      content: (
        <Container header={<Header variant="h2">Connect Partner Central</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              Connect your Partner Central account via OAuth. Your credentials are stored
              in your AWS Secrets Manager — never transmitted to GDNA.
            </Box>
            {productCode !== 'aeos-pc3' ? (
              <Alert type="info">
                Partner Central connection is optional for {productName}. You can skip this step.
              </Alert>
            ) : (
              <>
                <FormField label="Partner Central OAuth" description="Click to authorize">
                  <Button
                    variant="primary"
                    disabled={state.pcConnected}
                    onClick={() => setState((s) => ({ ...s, pcConnected: true }))}
                    iconName="external"
                  >
                    {state.pcConnected ? 'Connected' : 'Connect Partner Central'}
                  </Button>
                </FormField>
                {state.pcConnected && (
                  <StatusIndicator type="success">Partner Central connected</StatusIndicator>
                )}
              </>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: productCode !== 'aeos-pc3',
    },
    {
      title: 'Role Discovery',
      description: 'Discover existing users and IAM mappings',
      content: (
        <Container header={<Header variant="h2">Role Discovery & IAM Mapping</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              We'll scan your Partner Central users and IAM policy assignments to identify
              misalignments and recommend least-privilege configurations.
            </Box>
            {!state.roleDiscoveryRun ? (
              <Button
                variant="primary"
                onClick={() =>
                  setState((s) => ({
                    ...s,
                    roleDiscoveryRun: true,
                    roleDiscoveryResults: [
                      '3 users discovered',
                      '1 user over-permissioned (FullAccess on read-only role)',
                      '1 user missing AWSPartnerCentralOpportunityManagement',
                    ],
                  }))
                }
              >
                Run Discovery
              </Button>
            ) : (
              <SpaceBetween size="s">
                <StatusIndicator type="success">Discovery complete</StatusIndicator>
                {state.roleDiscoveryResults.map((r, i) => (
                  <Box key={i} variant="p">• {r}</Box>
                ))}
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: productCode !== 'aeos-pc3',
    },
    {
      title: 'Gap Analysis',
      description: 'Review what needs attention',
      content: (
        <Container header={<Header variant="h2">Gap Analysis</Header>}>
          <SpaceBetween size="m">
            <Box variant="p">
              The gap analysis identifies what's not done, ranked by urgency, with the first
              item already assigned to you.
            </Box>
            {!state.gapAnalysisRun ? (
              <Button
                variant="primary"
                onClick={() =>
                  setState((s) => ({
                    ...s,
                    gapAnalysisRun: true,
                    gapAnalysisResults: [
                      'IAM policy alignment — 1 user needs policy update (assigned to you)',
                      'MP Seller registration — not yet completed',
                      'ACE pipeline sync — 0 deals synced, connect ACE to begin',
                    ],
                  }))
                }
              >
                Run Gap Analysis
              </Button>
            ) : (
              <SpaceBetween size="s">
                <StatusIndicator type="success">Analysis complete — 3 gaps found</StatusIndicator>
                {state.gapAnalysisResults.map((r, i) => (
                  <Box key={i} variant="p">
                    <strong>{i + 1}.</strong> {r}
                  </Box>
                ))}
                <ProgressBar value={100} label="Onboarding Progress" description="Complete — ready to launch" />
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      ),
      isOptional: productCode !== 'aeos-pc3',
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '40px auto', padding: '0 20px' }}>
      <Wizard
        i18nStrings={{
          stepNumberLabel: (n) => `Step ${n}`,
          collapsedStepsLabel: (n, total) => `Step ${n} of ${total}`,
          submitButton: 'Launch Dashboard',
          previousButton: 'Back',
          nextButton: 'Next',
          cancelButton: 'Save & Exit',
          optional: 'optional',
        }}
        steps={steps}
        activeStepIndex={currentStep}
        onNavigate={({ detail }) => {
          advanceStep(detail.requestedStepIndex);
        }}
        onSubmit={() => {
          advanceStep(6);
          window.location.href = '/ops/tenants';
        }}
        onCancel={() => {
          window.location.href = '/ops/tenants';
        }}
        isLoadingNextStep={saving}
      />
    </div>
  );
}
